import * as Sentry from '@sentry/react';
import type { JSX } from 'react';
import React, { memo, useCallback, useEffect, useRef } from 'react';
import { Outlet, useLocation, useParams } from 'react-router-dom';

import Fallback from '@/components/Fallback';
import PersonSidebarNav from '@/components/PersonSidebarNav';
import PersonToolbar from '@/components/PersonToolbar';
import usePerson from '@/hooks/usePerson';
import PersonChannels from '@/pages/Person/components/PersonChannels';
import PersonDeletedNotice from '@/pages/Person/components/PersonDeletedNotice';
import type { Person as IPerson } from '@/types/person';
import Alert from '@/ui/components/Alert';
import PersonHeaderSkeleton from '@/ui/components/LoadingSkeleton/components/PersonHeaderSkeleton';
import PersonHeader from '@/ui/components/PersonHeader';

import PersonContext from './PersonContext';

const Person = (): JSX.Element | null => {
  const location = useLocation();
  const receivedPersonRef = useRef<IPerson | undefined | null>(location.state?.person);
  const receivedPerson = receivedPersonRef.current;
  const { personId } = useParams();

  const isQueryEnabled = !!personId && !receivedPerson;

  const {
    handleUpdatePerson,
    usePersonQuery: { isPending, data: personResponse, refetch }
  } = usePerson({ id: personId, enabled: isQueryEnabled });

  // Need this to keep sync with server (or the received stale state will be there forever)
  useEffect(() => {
    receivedPersonRef.current = null;
  }, []);

  const renderPerson = useCallback(
    (person: IPerson) => {
      if (person.deleted_at) {
        return <PersonDeletedNotice person={person} />;
      }

      if (!person.jurisdiction_id && person.jurisdiction?.id) {
        person.jurisdiction_id = person.jurisdiction.id;
      }

      if (person.program_qualification && !person.program_qualification._program_id) {
        person.program_qualification._program_id =
          person.program_qualification.selected_program?.id || null;
      }

      return (
        <Sentry.ErrorBoundary
          key={location.pathname}
          fallback={({ error, resetError }) => (
            <Fallback error={error} resetError={resetError} personId={person.id} />
          )}
          showDialog>
          <PersonContext.Provider value={{ person, refetch, updatePerson: handleUpdatePerson }}>
            <PersonSidebarNav />
            <PersonHeader person={person} />
            <PersonToolbar person={person} />

            <PersonChannels>
              <Outlet />
            </PersonChannels>
          </PersonContext.Provider>
        </Sentry.ErrorBoundary>
      );
    },
    [handleUpdatePerson, location.pathname, refetch]
  );

  if (!personId) return <Alert level="error">No ID found to get person</Alert>;

  if (isPending && isQueryEnabled) {
    return <PersonHeaderSkeleton />;
  }

  if (receivedPerson) return renderPerson(receivedPerson);

  if (personResponse?.errorMessage) {
    return <Alert level="error">{personResponse.errorMessage}</Alert>;
  }

  if (personResponse?.data) return renderPerson(personResponse?.data);

  return null;
};

export default memo(Person);
